// src/data/immigrationGuides/longResidenceILR.js

const longResidenceILR = {
    slug: "long-residence-ilr",

    title: "10 Year Long Residence ILR Guide UK: Requirements and Process",

    seo: {
        metaTitle:
            "10 Year Long Residence ILR UK: Requirements & Process",
        metaDescription:
            "The UK 10 year Long Residence route to ILR: lawful residence, continuous residence, the Life in the UK Test, English requirements and application steps.",
        keywords: [
            "10 year ILR UK",
            "Long Residence ILR",
            "10 years lawful residence UK",
            "long residence settlement"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Long Residence ILR",
            url: "/long-residence-ilr"
        }
    ],

    introduction: `
The [Long Residence route](https://www.gov.uk/long-residence) allows eligible people who have lived lawfully in the UK
for a continuous period to apply for [Indefinite Leave to Remain (ILR)](https://www.gov.uk/indefinite-leave-to-remain).

This route is different from visa-specific settlement routes because it is based
on a person's overall lawful residence history in the UK.
`,

    sections: [
        {
            heading: "What is Long Residence ILR?",
            content: `
Long Residence ILR is a settlement route for applicants who have completed the
required period of lawful residence in the UK.

The route considers a person's immigration history rather than only their current
visa category.
`
        },

        {
            heading: "Who can apply?",
            content: `
Applicants generally need to show that they:

- have completed the required period of lawful residence
- meet continuous residence requirements
- meet knowledge of language and life requirements
- satisfy suitability requirements

The Home Office assesses applications according to the Immigration Rules.
`
        },

        {
            heading: "10 years lawful residence",
            content: `
The Long Residence route is commonly associated with 10 years of lawful residence
in the UK.

Applicants should carefully review whether all periods of residence qualify and
whether any periods cannot be counted.
`
        },

        {
            heading: "Continuous residence requirement",
            content: `
Applicants must meet continuous residence requirements.

Important factors include:

- absences from the UK
- gaps in immigration permission
- periods that may not count towards residence

Applicants should review their complete immigration history before applying.
`
        },

        {
            heading: "Life in the UK Test",
            content: `
Applicants normally need to pass the [Life in the UK Test](https://www.gov.uk/life-in-the-uk-test) unless exempt.

The test covers:

- British history
- government
- law
- traditions
- values
`
        },

        {
            heading: "English language requirement",
            content: `
Applicants normally need to prove knowledge of English unless they qualify for
an exemption.

Accepted evidence depends on individual circumstances.
`
        },

        {
            heading: "Application process",
            content: `
The application process normally involves:

- Completing an online application
- Providing supporting evidence
- Attending a biometric appointment if required
- Waiting for a decision
`
        }
    ],

    checklist: [
        "Confirm 10 years lawful residence",
        "Review all previous visas",
        "Check absences from the UK",
        "Confirm no gaps in lawful permission",
        "Pass Life in the UK Test",
        "Meet English requirement",
        "Prepare evidence"
    ],

    faqs: [
        {
            question: "What is the Long Residence ILR route?",
            answer:
                "It is a settlement route based on completing the required period of lawful continuous residence in the UK."
        },
        {
            question: "Do all visas count towards Long Residence?",
            answer:
                "Applicants should check the Immigration Rules because some periods may not qualify."
        },
        {
            question: "Do I need Life in the UK Test for Long Residence ILR?",
            answer:
                "Most applicants need to pass the Life in the UK Test unless exempt."
        }
    ],

    relatedPages: [
        {
            title: "ILR Guide",
            url: "/ilr-guide"
        },
        {
            title: "Life in the UK Test",
            url: "/mock-exams"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Long Residence",
            url: "https://www.gov.uk/long-residence"
        },
        {
            title: "GOV.UK - Immigration Rules",
            url: "https://www.gov.uk/guidance/immigration-rules"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-20"
    }
};

export default longResidenceILR;